// Anita Week 2 / Day 4 — Money, Change and Compare (U.S. Grade 3).
// Math: 100 − (37 + 3×8), 50 − 34 − 9 change chain, compare 4×6 vs 3×9,
// budget 75 against 28+12+31. Russian RU 1–2.
// SAFETY: parentGuide.safetyNotes — paper money simulation only.

const day4 = {
  id: 'day-4',
  studentId: 'anita',
  weekId: 'week-2',
  dayId: 'day-4',
  status: 'active',
  title: 'Деньги, сдача и сравнение / Money, Change and Compare',
  shortTitle: 'Деньги и сдача / Money and Change',
  theme:
    'Считать сдачу важно: иначе можно потерять нимы. / Counting change matters: otherwise you can lose nims.',
  estimatedTimeLabel: '60–90 минут / 60–90 minutes',
  lessonTiming: { minMinutes: 60, maxMinutes: 90, breaks: 2 },
  bilingualMode: 'ru-primary-en-helper',
  courseLayers: ['reading', 'math', 'logic', 'spelling', 'creative', 'speech', 'finance-safety'],
  readingLevel: 'grade-2',
  nimnyIntro:
    'Нимни приносит маленькую бумажную лавку. Сегодня Анита покупает, считает сдачу и сравнивает два набора. / Nimny brings a small paper shop. Today Anita buys, counts change, and compares two sets.',
  goals: [
    'Считать стоимость покупки с умножением. / Compute purchase cost with multiplication.',
    'Считать сдачу в две операции. / Compute change in two steps.',
    'Сравнивать два набора по стоимости. / Compare two sets by cost.',
    'Проверять, хватит ли бюджета 75. / Check if a budget of 75 is enough.',
    'Объяснять, почему важно считать сдачу.'
  ],
  vocabulary: [
    { term: 'сдача / change', definition: 'нимы, которые возвращают после покупки. / nims returned after a purchase.' },
    { term: 'купить / to buy', definition: 'отдать нимы за вещь. / give nims for a thing.' },
    { term: 'хватит / enough', definition: 'когда нимов достаточно. / when there are enough nims.' },
    { term: 'осталось / remained', definition: 'сколько нимов осталось. / how many nims remain.' },
    { term: 'дороже / more expensive', definition: 'когда нужно отдать больше нимов. / when you give more nims.' }
  ],
  onlineTasks: [
    {
      id: 'reading-words', type: 'text',
      title: 'Слова дня / Words of the Day',
      prompt: 'Прочитай: сдача, купить, хватит, осталось, дороже.',
      ruPrimaryText: 'Прочитай по-русски: сдача, купить, хватит, осталось, дороже.',
      enHelpText: 'Read in Russian: change, to buy, enough, remained, more expensive.',
      hint: 'Сначала русское чтение. / Russian first.',
      answer: 'Попытка чтения 5 русских слов.'
    },
    {
      id: 'reading-sentences', type: 'text',
      title: 'Маленькое чтение / Short Reading',
      prompt:
        'Прочитай по-русски:\n1. Я считаю сдачу.\n2. Я проверяю, хватит ли нимов.\n3. Один набор может быть дороже другого.\n4. Полезно оставлять часть нимов.',
      ruPrimaryText: 'Прочитай 4 короткие фразы про сдачу.',
      enHelpText:
        '1. I count my change.\n2. I check if I have enough nims.\n3. One set can be more expensive than another.\n4. It is useful to keep some nims aside.',
      answer: 'Попытка чтения 4 русских фраз.'
    },
    {
      id: 'math-1-purchase-with-multi',
      type: 'math',
      title: 'Math 1 — Покупка в лавке / Shop Purchase',
      prompt:
        'У Аниты 100 нимов.\nОна купила книгу за 37 нимов и 3 карандаша по 8 нимов.\nСколько нимов осталось?',
      ruPrimaryText: '3 × 8 = ?. 37 + ? = ?. 100 − ? = ?',
      enHelpText: '100 nims. Book 37 + 3 pencils at 8 each. How many nims remain?',
      helper: 'Сначала 3 × 8, потом сложи с 37, потом 100 минус. / Multiply, then add, then subtract.',
      answer: '39',
      unit: 'нимов / nims'
    },
    {
      id: 'math-2-change-chain',
      type: 'math',
      title: 'Math 2 — Сдача / Change',
      prompt:
        'Анита дала 50 нимов за набор, который стоит 34 нима.\nПотом она купила наклейку за 9 нимов из сдачи.\nСколько нимов осталось от сдачи?',
      ruPrimaryText: '50 − 34 = ?. ? − 9 = ?',
      enHelpText: '50 nims for a set worth 34. Then she buys a sticker for 9 from the change. How many nims remain from the change?',
      helper: 'Сначала сдача, потом новая покупка. / Change first, then second purchase.',
      answer: '7',
      unit: 'нимов / nims'
    },
    {
      id: 'math-3-compare-two-sets',
      type: 'math',
      title: 'Math 3 — Два набора / Two Sets',
      prompt:
        'Набор A: 4 карточки по 6 нимов.\nНабор B: 3 карточки по 9 нимов.\nКакой набор дороже и на сколько?\n\nЗапиши ответ через /: какой / на сколько. Например: B / 3.',
      ruPrimaryText: 'A = 4 × 6. B = 3 × 9. Какой дороже и на сколько?',
      enHelpText: 'A: 4 × 6. B: 3 × 9. Which set is more expensive and by how many nims?',
      helper: 'Перемножь и сравни. / Multiply each, then compare.',
      answer: 'B / 3'
    },
    {
      id: 'math-4-budget-75',
      type: 'math',
      title: 'Math 4 — Хватит ли бюджета / Is the Budget Enough',
      prompt:
        'У Аниты 75 нимов.\nОна хочет купить:\n— блокнот за 28;\n— ручку за 12;\n— книгу за 31.\nХватит ли нимов?\nСколько останется или сколько не хватит?\n\nЗапиши ответ через /: хватит-или-нет / число. Например: хватит / 4.',
      ruPrimaryText: '28 + 12 + 31 = ?. 75 − ? = ?',
      enHelpText: '75 nims. Notebook 28 + pen 12 + book 31. Enough? Format as enough-or-not / number.',
      helper: 'Сначала сумма, потом 75 минус сумма. / Sum first, then subtract.',
      answer: 'хватит / 4'
    },
    {
      id: 'logic-buy-all-or-keep',
      type: 'text',
      title: 'Логика — купить всё или оставить / Logic — Buy All or Save',
      prompt:
        'Что умнее: купить всё сразу или оставить часть нимов на потом?\nОтветь 1–2 предложениями по-русски и объясни почему.',
      ruPrimaryText: 'Купить всё сразу или оставить часть? Почему?',
      enHelpText: 'Is it smarter to buy everything at once or to keep some nims aside? Answer in 1–2 Russian sentences and explain.',
      placeholder: 'Умнее оставить часть, потому что ...',
      answer: 'Принимается ответ с объяснением «остаётся остаток на новые покупки», «вдруг нужно срочно купить ещё что-то», «лучше иметь запас».'
    },
    {
      id: 'spelling-change',
      type: 'text',
      title: 'Правописание / Spelling',
      prompt: 'Напиши: сдача, купить, хватит, осталось.\nВставь буквы: сда_а, куп_ть, хв_тит, ост_лось.',
      ruPrimaryText: 'Напиши слова и вставь буквы.',
      enHelpText: 'Write and fill in: сдача, купить, хватит, осталось.',
      answer: 'сдача, купить, хватит, осталось.'
    },
    {
      id: 'creative-my-purchase',
      type: 'text',
      title: 'Моя покупка-проект / My Purchase-Project',
      prompt:
        'Нарисуй или составь свою покупку на 60 нимов.\nЗаполни:\n— вещь 1: ___ за ___ нимов\n— вещь 2: ___ за ___ нимов\n— вещь 3: ___ за ___ нимов\nСумма: ___\nСдача: 60 − ___ = ___',
      ruPrimaryText: 'Нарисуй покупку-проект и посчитай сдачу.',
      enHelpText: 'Draw or build your 60-nim purchase, then compute the change.',
      answer: 'Open-ended. Принимается, если сумма ≤ 60 и сдача правильная.'
    },
    {
      id: 'speech-explain-change',
      type: 'text',
      title: 'Объясни родителю / Explain to Parent',
      prompt: 'Скажи по-русски: «Сдачу считать полезно, потому что …».',
      ruPrimaryText: 'Скажи по-русски одну фразу про сдачу.',
      enHelpText: 'Say in Russian: "Counting change is useful because …".',
      answer: 'Принимается любая осмысленная русская фраза.'
    }
  ],
  notebookInstructions: [
    'Если нет принтера:',
    '1. Напиши 5 слов дня.',
    '2. Реши Math 1–4 с шагами.',
    '3. Объясни, что умнее: купить всё или оставить часть.',
    '4. Напиши 4 слова на правописание.',
    '5. Составь покупку-проект на 60 нимов.',
    '6. Скажи родителю: «Сдачу считать полезно, потому что …».'
  ],
  printWorksheet: {
    title: 'Деньги, сдача и сравнение / Money, Change and Compare',
    subtitle: 'Неделя 2 · День 4 / Week 2 · Day 4',
    pages: [
      {
        title: 'Маршрут дня · Слова · Чтение / Route, Words and Reading',
        blocks: [
          { type: 'text', text: 'Миссия: Анита считает сдачу и сравнивает наборы. / Mission.' },
          { type: 'list', items: [
            'Слова дня: сдача, купить, хватит, осталось, дороже. / Words.',
            'Маленькое чтение — 4 короткие фразы про сдачу (см. экран). / Reading.',
            'English help мелким шрифтом. / English help.'
          ] }
        ]
      },
      {
        title: 'Математика денег / Money Math',
        blocks: [
          { type: 'text', text: 'Показывай шаги и единицы. / Show steps and units.' },
          { type: 'list', items: [
            'Math 1. 3 × 8 = ___; 37 + ___ = ___; 100 − ___ = ___ нимов. / Purchase with multiplication.',
            'Math 2. 50 − 34 = ___; ___ − 9 = ___ нимов от сдачи. / Change chain.',
            'Math 3. A = 4 × 6 = ___; B = 3 × 9 = ___; дороже ___ на ___. / Compare sets.',
            'Math 4. 28 + 12 + 31 = ___; 75 − ___ = ___. Хватит ли? ___ / Budget 75 check.',
            'Логика: купить всё сразу или оставить часть? 1–2 предложения. / Logic.'
          ] }
        ]
      },
      {
        title: 'Правописание, моя покупка-проект и parent check / Spelling, My Purchase-Project and Parent Check',
        blocks: [
          { type: 'list', items: [
            'Правописание: напиши сдача, купить, хватит, осталось. Вставь буквы: сда_а, куп_ть, хв_тит, ост_лось. / Spelling.',
            'Составь покупку-проект на 60 нимов: 3 вещи + сумма + сдача 60 − ___ = ___. / Purchase-project.',
            'Скажи родителю: «Сдачу считать полезно, потому что …». / Say in Russian.',
            'Parent Check: × для умножения, цепочка сдачи, сравнение двух произведений, остаток бюджета. / Parent Check.',
            'Notebook Mode fallback. / Notebook Mode fallback.'
          ] }
        ]
      }
    ]
  },
  parentGuide: {
    goal: 'Анита считает покупку и сдачу в две операции, сравнивает два набора и проверяет бюджет.',
    mathGoal: 'U.S. Grade 3: × как первый шаг внутри покупки, цепочка − − для сдачи, сравнение произведений 4×6 vs 3×9, бюджет 75 vs сумма из трёх.',
    russianGoal: 'Прочитать слова: сдача, купить, хватит, осталось, дороже.',
    readingWritingGoal: 'Прочитать 4 фразы, написать 4 слова, составить покупку-проект.',
    practicalGoal: 'Объяснить, почему важно считать сдачу.',
    whatThisLessonTeaches: 'Анита учится считать сдачу как «деньги назад» и сравнивать стоимость двух наборов.',
    keyMessage: 'Считай сдачу. Дороже — не всегда лучше. Сначала проверь бюджет.',
    howToExplain: 'Возьмите бумажные нимы и две группы предметов. Покажите, что 4 × 6 = 3 × 8 = 24, но 3 × 9 = 27.',
    exampleDialogue: 'Parent: Сколько у тебя останется от 50, если набор стоит 34?\nChild: 16.\nParent: А если потом купить наклейку за 9?\nChild: 16 − 9 = 7.',
    readingSupport: 'Слово «осталось» — по слогам.',
    russianSpeechGoal: 'Анита говорит: «Сдачу считать полезно, потому что я знаю, сколько у меня осталось.»',
    mathAnswerKey:
      'Math 1: 3 × 8 = 24; 37 + 24 = 61; 100 − 61 = 39.\nMath 2: 50 − 34 = 16; 16 − 9 = 7.\nMath 3: A = 4 × 6 = 24; B = 3 × 9 = 27; B дороже на 3.\nMath 4: 28 + 12 + 31 = 71; 75 − 71 = 4; хватит, остаток 4.',
    spellingAnswerKey: 'сдача\nкупить\nхватит\nосталось',
    answerKey: [
      { taskId: 'reading-words', answer: 'Answer: попытка чтения 5 русских слов.\nSolution: Анита читает по очереди.\nParent note: Засчитывать честную попытку.' },
      { taskId: 'reading-sentences', answer: 'Answer: попытка чтения 4 фраз про сдачу.\nSolution: Каждая фраза — отдельно.\nParent note: Английский — только для смысла.' },
      { taskId: 'math-1-purchase-with-multi', answer: 'Answer: 39 нимов.\nSolution: 3 × 8 = 24; 37 + 24 = 61; 100 − 61 = 39.\nParent note: Двухшаговая задача с умножением.' },
      { taskId: 'math-2-change-chain', answer: 'Answer: 7 нимов.\nSolution: 50 − 34 = 16; 16 − 9 = 7.\nParent note: Сначала сдача, потом новая покупка.' },
      { taskId: 'math-3-compare-two-sets', answer: 'Answer: B / 3.\nSolution: A = 24; B = 27; 27 − 24 = 3.\nParent note: Сравнение двух произведений.' },
      { taskId: 'math-4-budget-75', answer: 'Answer: хватит / 4.\nSolution: 28 + 12 + 31 = 71; 75 − 71 = 4.\nParent note: Трёхсоставная покупка и остаток.' },
      { taskId: 'logic-buy-all-or-keep', answer: 'Answer: умнее оставить часть нимов.\nSolution: Остаток помогает на новые расходы или цели.\nParent note: Принимается любое объяснение с этой идеей.' },
      { taskId: 'spelling-change', answer: 'Answer: сдача, купить, хватит, осталось.\nSolution: Вставленные буквы: ч, и, а, а.\nParent note: Мягкие ошибки допустимы.' },
      { taskId: 'creative-my-purchase', answer: 'Answer: покупка-проект на 60 нимов с тремя вещами и сдачей.\nSolution: Сумма ≤ 60 и сдача = 60 − сумма.\nParent note: Главное — арифметика правильная.' },
      { taskId: 'speech-explain-change', answer: 'Answer: одна осмысленная русская фраза про сдачу.\nSolution: «Сдачу считать полезно, потому что я знаю, сколько у меня осталось.»\nParent note: Главное — мысль.' }
    ],
    check: [
      'Анита прочитала 5 слов и 4 фразы.',
      'Анита решила Math 1–4 с шагами.',
      'Анита объяснила, что умнее: купить всё или оставить часть.',
      'Анита написала 4 слова и составила покупку-проект.',
      'Анита сказала фразу про сдачу.'
    ],
    spellingCheck: 'Проверить буквы в словах сдача, купить, хватит, осталось.',
    readingCheck: 'Засчитывать попытку чтения.',
    practicalCheck: 'Анита делит покупку на цену и остаток.',
    drawingCriteria: 'Покупка-проект имеет 3 вещи, сумму и сдачу.',
    safetyNotes:
      'Это бумажная симуляция: используются только нимы и игрушечные предметы. Не работайте с реальными купюрами, картами, номерами карт или CVV. Не открывайте ребёнку реальный счёт или кошелёк. Все решения о реальных подарках принимает родитель отдельно от этой игры.',
    commonMistakes: [
      '«Сдача = вся сумма.» Правильно: сдача = «деньги назад» после покупки.',
      '«Больше карточек = всегда дороже.» Правильно: 4 × 6 = 24, но 3 × 9 = 27.',
      'Складывать только две покупки из трёх. Правильно: сложить все три.'
    ],
    discussionPrompts: [
      'Зачем считать сдачу?',
      'Что важнее: больше карточек или меньше карточек, но за более крупную цену?',
      'Когда полезно оставить часть нимов?'
    ],
    ifEasy: 'Усложнение: 120 нимов; купила 2 книги по 25 и 3 наклейки по 7. Остаток? (Ответ: 120 − 50 − 21 = 49.)',
    ifDifficult: 'Оставьте Math 1 и Math 2.',
    rewardGuidance: 'Базовая награда 3 нима. Бонус за самостоятельную цепочку сдачи или сравнение двух наборов.',
    parentTheoryCard: {
      title: 'Сдача и сравнение (Day 4) / Change and Compare',
      purpose: 'Научить ребёнка считать «деньги назад» и сравнивать два набора.',
      keyMessage: 'Считать сдачу = знать, сколько у меня осталось.',
      parentScript: 'Возьмите 50 бумажных нимов. Покажите, что после покупки за 34 у вас остаётся 16. Потом купите наклейку — останется 7.',
      practiceSteps: ['Прочитайте слова и фразы.', 'Решите Math 1–4 со шагами.', 'Сравните A и B.', 'Составьте 60-нимовую покупку.'],
      doNotSay: ['Не говорите «нимы можно поменять на доллары».', 'Не сравнивайте нимы с реальной зарплатой.', 'Не ругайте за ошибку в копейках.'],
      paperOnlyNotes: 'Нимы — это бумажные токены, не настоящие деньги. Не использовать реальные карты или CVV в этой игре.'
    },
    parentOnlyNotes: 'Не превращайте задачу в реальную покупку. Никаких реальных карт.',
    tokenReward: 'Нимы — это призовые токены курса, а не оплата.'
  },
  reward: {
    unit: 'nim', unitName: 'ним', base: 3, max: 5,
    label: 'Награда за миссию', display: '3 нима',
    bonuses: [
      { id: 'change-chain', label: 'Бонус за самостоятельную цепочку сдачи', amount: 1 },
      { id: 'compare-two-sets', label: 'Бонус за сравнение двух наборов', amount: 1 }
    ]
  },
  completionRequirements: {
    requiredTaskIds: [
      'reading-words', 'reading-sentences',
      'math-1-purchase-with-multi', 'math-2-change-chain',
      'math-3-compare-two-sets', 'math-4-budget-75',
      'logic-buy-all-or-keep', 'spelling-change',
      'creative-my-purchase', 'speech-explain-change'
    ],
    allowNotebookCompletion: true
  },
  nextLessonId: 'anita-week-2-day-5'
};

export default day4;
